/**
 * 海报创建页外壳 —— UX-1(2026-05-30)。返回链接 + 标题区 + 内容容器。
 * 2026-05-31 调整:段控(文字/图改图)下沉到 `FreeCreateBody`,与「保存草稿」同一行
 *   (justify-between),消除「段控一行、保存草稿另一行、中间留空」的不协调。模板模式
 *   本就无段控。各创建流(FreeCreateBody / TemplateCreateBody)作为 children 渲染,自带 fixed footer。
 */
import { ArrowLeft, Wand2 } from "lucide-react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/** 自由创作的两种模式(波A:模板套用独立模式已随套模板路删除)。 */
export type CreateTab = "text" | "i2i";

interface Props {
  /**
   * 2026-08-01 —— 页头按路分文案。
   *
   * ★改图**不走归整层**(I2I 分支给 `PromptPanel` 传了 `hidePolish`),它跟文字那条
   *   不是同一种东西:一条是「从零造一张」,一条是「改我手上这张」。三条路共用
   *   一句「视觉舱 · 创作」时,进改图页看到的是别人的标题。
   */
  tab: CreateTab;
  /**
   * 2026-08-01 —— 带着进页的用途(`form.sceneKey`,没有则 null)。
   *
   * ★★页头认**用途**,`tab` 只做兜底。商品主图挂上产品图之后确实是 i2i,但它
   *   <b>不是「改这张图」</b> —— 用户没上传过任何东西,底图是产品库里那张。
   *   `isI2I` 此前同时在回答三个问题:①引擎走 image_edit 还是 text2image
   *   ②请求体带不带 coverImageRef ③页面叫什么。前两个归模式没错,**第三个归用途**。
   *   这与 `sceneUsageOf`(用途进归整)是同一条原则:判据钉在用途上,不钉在机制上。
   */
  sceneKey?: string | null;
  children: ReactNode;
}

/**
 * 用途 → 页头文案。只收**这条路的脸与模式不一致**的用途;其余的走 tab 兜底。
 *
 * ★不做成 i18n key:这三句是产品承诺(与卡墙卡面逐字对应),放在这里能和
 *   `catalog.ts` 的 title/usage 一起被读到;摊进 poster.json 反而看不出它在复述哪张卡。
 */
const SCENE_HEADINGS: Record<string, { title: string; subtitle: string }> = {
  poster_single: {
    title: "商品主图",
    subtitle: "选个商品,用它的实拍图当底,出一张能直接上架的主图",
  },
};

export function PosterCreateShell({ tab, sceneKey = null, children }: Props) {
  const { t } = useTranslation("poster");
  const navigate = useNavigate();
  const isI2I = tab === "i2i";
  const scene = sceneKey ? SCENE_HEADINGS[sceneKey] : undefined;
  const title = scene ? scene.title : isI2I ? t("create.i2iTitle") : t("create.title");
  const subtitle = scene ? scene.subtitle : isI2I ? t("create.i2iSubtitle") : t("create.subtitle");

  return (
    <>
      {/* pb 从 32 收到 6:底栏改 sticky 后自己占流内位置,原来那 128px 是给 fixed 底栏
          让出来的空档,再留着就是底栏下方一片空白。 */}
      <div className="space-y-5 pb-6">
        <button
          onClick={() => navigate("/ai-haibao")}
          className="-ml-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-card/40 hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("create.back")}
        </button>

        <div className="flex items-center gap-3 fx-enter">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <Wand2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{title}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        {children}
      </div>
    </>
  );
}
